import { Box } from '@mui/material';
import React, { useCallback, useMemo } from 'react';

import { GLOBAL_HERO_SECTION_IMAGE, HERO_HEIGHT_MODE_TABS } from '../../../../constants/templates';
import { MAX_IMAGE_SIZE_BYTES, MAX_IMAGE_SIZE_BYTES_MB, VISUAL_MEDIA_TYPES } from '../../../../constants/upload';
import { useTemplateUploadHandlers } from '../../../../hooks/useTemplateUploadHandlers';
import { ImageFitEnum, ISegmentedControlOption } from '../../../../types/shell';
import { IBaseUploadControlProps, IImageUrlWithFitValueProps } from '../../../../types/templates';
import { CSSObjectFitEnum } from '../../../../types/upload';
import { ResetPlaceholder, SegmentedControl } from '../../_controls_';
import { ImageUploader } from '../FileUploader/ImageUploader';
import { FormLabel } from '../FormLabel';

interface IProps extends IBaseUploadControlProps, IImageUrlWithFitValueProps {
  defaultImageUrl?: string;
  defaultImageFit?: ImageFitEnum;
  onImageDrop?: (files: File[]) => void;
  onImageUpload?: (url: string) => void;
  onImageRemove: () => void;
  onImageFitChange: (fit: ImageFitEnum) => void;
  onClearImageOverride?: () => void;
  onResetImageToGlobal?: () => void;
}

export const BackgroundImageUploader: React.FC<IProps> = ({
  mode,
  globalImageUrl,
  overrideImageUrl,
  defaultImageUrl,
  defaultImageFit,
  globalImageFit,
  overrideImageFit,
  onImageDrop: onImageDropProp,
  onImageUpload,
  onImageRemove,
  onImageFitChange,
  onClearImageOverride,
  onResetImageToGlobal,
  dataAction,
}) => {
  const {
    isPageOverrideMode,
    isOverridden: isImageOverridden,
    handleDrop,
  } = useTemplateUploadHandlers({
    mode,
    overrideValue: overrideImageUrl,
    fieldName: GLOBAL_HERO_SECTION_IMAGE,
    onDrop: onImageDropProp,
    onUpload: onImageUpload,
    onRemove: onImageRemove,
    onClearOverride: onClearImageOverride,
  });

  const displayUrl = useMemo(() => {
    if (isImageOverridden) {
      return overrideImageUrl;
    }
    return globalImageUrl || '';
  }, [isImageOverridden, overrideImageUrl, globalImageUrl]);

  // image_fit carries the hero height mode: Cover = Auto (content-driven height with a
  // fixed floor), Contain = Adapt (hero tracks the image aspect on the claim page).
  const displayImageFit = useMemo(() => {
    if (isPageOverrideMode && overrideImageFit !== undefined) {
      return overrideImageFit;
    }
    return globalImageFit || ImageFitEnum.Cover;
  }, [isPageOverrideMode, overrideImageFit, globalImageFit]);

  const handleRemoveClick = useCallback(() => {
    onImageRemove();
  }, [onImageRemove]);

  const handleResetClick = useCallback(() => {
    if (isPageOverrideMode) {
      // Clear both image and framing overrides atomically to fall back to defaults modal value
      onResetImageToGlobal?.();
    } else if (defaultImageUrl !== undefined) {
      // In global mode, reset to blueprint default
      onImageUpload?.(defaultImageUrl);
      if (defaultImageFit !== undefined) {
        onImageFitChange(defaultImageFit);
      }
    }
  }, [isPageOverrideMode, onResetImageToGlobal, onImageUpload, defaultImageUrl, defaultImageFit, onImageFitChange]);

  const showResetButton = useMemo(() => {
    if (isPageOverrideMode) {
      // In page override mode, "default" is the global value set in the defaults modal.
      // Show reset when there's an override that differs from the global.
      if (!isImageOverridden) {
        return false;
      }
      return overrideImageUrl !== globalImageUrl;
    }
    // In global mode, "default" is the blueprint value.
    if (!defaultImageUrl) {
      return false;
    }
    return globalImageUrl !== defaultImageUrl;
  }, [isPageOverrideMode, isImageOverridden, overrideImageUrl, globalImageUrl, defaultImageUrl]);

  const showPlaceholderReset = showResetButton && !displayUrl;

  const placeholderFooter = useMemo(() => {
    if (!showPlaceholderReset) {
      return undefined;
    }
    return (
      <ResetPlaceholder
        message="Background image removed. No background image will be shown."
        onClick={handleResetClick}
        dataAction={`${dataAction}_reset`}
      />
    );
  }, [showPlaceholderReset, handleResetClick, dataAction]);

  // Tile preview mirrors the framing: Auto crops like the hero (cover), Adapt shows
  // the full image (contain) since the hero height follows the image in that mode.
  const tileObjectFit = useMemo(
    () => (displayImageFit === ImageFitEnum.Contain ? CSSObjectFitEnum.Contain : CSSObjectFitEnum.Cover),
    [displayImageFit],
  );

  const heightModeOptions: ISegmentedControlOption<ImageFitEnum>[] = useMemo(
    () =>
      HERO_HEIGHT_MODE_TABS.map((tab) => ({
        label: tab.label,
        value: tab.value,
        dataAction: `${dataAction}_hero_height_${tab.value}_btn`,
      })),
    [dataAction],
  );

  const uploaderConfig = useMemo(() => {
    return {
      height: 230,
      title: 'Click to upload or drag an image',
      helperText: `SVG, PNG, JPG or GIF (max. ${MAX_IMAGE_SIZE_BYTES_MB}MB)`,
      accept: VISUAL_MEDIA_TYPES,
      maxSize: MAX_IMAGE_SIZE_BYTES,
      fileUrl: displayUrl || undefined,
      onDrop: handleDrop,
      onRemove: displayUrl ? handleRemoveClick : undefined,
      fileSx: { objectFit: tileObjectFit },
      placeholderFooter,
      dataAction,
    };
  }, [displayUrl, tileObjectFit, handleDrop, handleRemoveClick, dataAction, placeholderFooter]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <ImageUploader {...uploaderConfig} />
      <FormLabel label="Hero height">
        <SegmentedControl
          options={heightModeOptions}
          value={displayImageFit}
          onChange={onImageFitChange}
          dataAction={`${dataAction}_hero_height`}
        />
      </FormLabel>
    </Box>
  );
};
